import { useCallback, useEffect, useMemo, useState } from 'react'

import { InjectionTokens } from '@/controller/tokens'

import { Navaid } from '@/domain/models/Waypoint/Navaid'
import { MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'

import { INavaidRepository } from '@/data/NavaidRepository/INavaidRepository'

import { makeGeoJsonNavaidFeatureCollection } from '../components/MapNavaid/makeGeoJsonNavaidFeatureCollection'
import { MapNavaid } from '../components/MapNavaid/MapNavaid'
import { makeNavaidFeatureInfoData, NavaidFeatureInfoData } from '../components/MapNavaid/navaidFormatters'
import { MIN_NAVAID_LABEL_ZOOM } from '../components/MapNavaid/navaidLayerSpecs'
import { useMapContext } from '../components/MapView/MapContext'
import { MapImperativeInterface } from '../interfaces/MapImperativeInterface'
import { FeatureInfoState, FeatureInfoStateMutator } from '../states/FeatureInfoState'
import { getFeatureFlyToPadding, ScreenElementsStateMutator } from '../states/ScreenElementsState'

import type { ILogger } from '@/infra/logger/ILogger'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'

function getActiveNavaidId(): string | undefined {
  const active = FeatureInfoState.getValue().active
  return active?.type === 'navaid' ? active.navaid.id : undefined
}

function MapNavaidPresenter() {
  const [mapLayerState] = useGlobalState<MapLayerState, MapLayerStateProps>(InjectionTokens.MapLayerState)
  const navaidRepository = useInjection<INavaidRepository>(InjectionTokens.NavaidRepository)
  const logger = useInjection<ILogger>(InjectionTokens.Logger)
  const { map } = useMapContext()

  const showNavaids = mapLayerState.navaids

  const [selectedNavaidId, setSelectedNavaidId] = useState<string | undefined>(getActiveNavaidId)

  const navaids = useMemo<Navaid[]>(() => {
    if (!showNavaids) return []
    return navaidRepository.getAllNavaids()
  }, [navaidRepository, showNavaids])

  const featureCollection = useMemo(
    () => makeGeoJsonNavaidFeatureCollection(navaids, selectedNavaidId),
    [navaids, selectedNavaidId]
  )

  // Clear local selection and panel when the layer is turned off.
  useEffect(() => {
    if (showNavaids) return

    setSelectedNavaidId(undefined)

    if (FeatureInfoState.getValue().active?.type === 'navaid') {
      FeatureInfoStateMutator.hide()
      ScreenElementsStateMutator.setFeatureInfoPanel(false)
    }
  }, [showNavaids])

  // Sync local selection with the panel so external close (ESC, map click, other feature panel)
  // and presenter remount on style reload converge to the panel as the source of truth.
  useEffect(() => {
    const subscription = FeatureInfoState.subscribe((state) => {
      const activeNavaidId = state.active?.type === 'navaid' ? state.active.navaid.id : undefined
      setSelectedNavaidId((prev) => (prev === activeNavaidId ? prev : activeNavaidId))
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleImageLoadError = useCallback(() => {
    logger.warn('MapNavaid', 'Could not register navaid images')
  }, [logger])

  const handleNavaidClick = useCallback(
    (navaid: NavaidFeatureInfoData) => {
      const target = navaids.find((wpt) => wpt.id.toString() === navaid.id)
      if (!target) return

      // Toggle: clicar no mesmo auxílio já selecionado fecha o painel.
      if (selectedNavaidId === navaid.id) {
        setSelectedNavaidId(undefined)
        FeatureInfoStateMutator.hide()
        ScreenElementsStateMutator.setFeatureInfoPanel(false)
        return
      }

      setSelectedNavaidId(navaid.id)

      // O clique do Mapbox só carrega os campos básicos (projectNavaidFeature descarta
      // purpose/operationalHours/remarks/frequencyUom); reconstruímos os dados completos
      // a partir do modelo Navaid para o painel exibir todas as seções.
      const navaidData = makeNavaidFeatureInfoData(target)

      FeatureInfoStateMutator.showNavaid(`${navaidData.code} ${navaidData.navaidType}`, navaidData)
      ScreenElementsStateMutator.setFeatureInfoPanel(true)

      // Recentraliza o auxílio na área visível útil, descontando o painel recém-aberto. Preserva o
      // zoom atual quando já estiver no nível mínimo de label (ou acima) para não afastar a câmera.
      const currentZoom = map?.getZoom?.()
      const targetZoom =
        typeof currentZoom === 'number' ? Math.max(currentZoom, MIN_NAVAID_LABEL_ZOOM) : MIN_NAVAID_LABEL_ZOOM
      MapImperativeInterface.flyToCoordinates(navaid.coordinates, targetZoom, 500, getFeatureFlyToPadding())
    },
    [map, navaids, selectedNavaidId]
  )

  if (!showNavaids) return null

  return (
    <MapNavaid
      data={featureCollection}
      selectedNavaidId={selectedNavaidId}
      onImageLoadError={handleImageLoadError}
      onNavaidClick={handleNavaidClick}
      onLabelClick={handleNavaidClick}
    />
  )
}

export { MapNavaidPresenter }
